import { canv } from "@/canv/global";
import { sortByIndexByParent } from "@/canvasController/helpers";
import { frameWidget } from "@/elements/frame";
import { frameOverlappedManager } from "@/elements/frame/FrameOverlappedManager";
import { Group } from "@/group";

/**
 * Проверяет, есть ли внутри структуры группы хотя бы один frameWidget
 */
export const isFrameInStructure = (root: Group): boolean => {
    if (!root) return false;

    const stack = [root];

    while (stack.length > 0) {
        const current = stack.pop();
        if (!current?.children) continue;

        for (const child of current.children) {
            if (child instanceof frameWidget) return true;
            if (child instanceof Group) stack.push(child);
        }
    }

    return false;
};

/**
 * Проверяет, был ли клик по подгруппе, не содержащей frameWidget
 * (в пределах объединённой группы)
 */
export const isClickOnNonFrameInsideGroup = (group: Group): boolean => {
    if (!group?.children?.length) return false;

    const hasFrameInside = (subgroup: Group): boolean => {
        for (const child of subgroup.children) {
            if (child instanceof frameWidget) return true;
            if (child instanceof Group && hasFrameInside(child)) return true;
        }
        return false;
    };

    for (let i = group.children.length - 1; i >= 0; i--) {
        const childGroup = group.children[i];
        if (!(childGroup instanceof Group)) continue;

        if (hasFrameInside(childGroup)) continue;

        if (childGroup.isOverAnyInGroup()) {
            return true;
        }
    }

    return false;
};

/**
 * Рекурсивно ищет первый frameWidget внутри структуры группы
 */
export const findFirstFrameWidget = (group: Group): frameWidget | null => {
    if (!group?.children?.length) return null;

    for (const child of group.children) {
        if (child instanceof frameWidget) return child;

        if (child instanceof Group) {
            const result = findFirstFrameWidget(child);
            if (result) return result;
        }
    }

    return null;
};

export const cantShiftByLock = (foundCandidate?: Group) => {
    return foundCandidate?.isLocked || canv.canvasController.selected?.[0]?.isLocked;
};

export const calcCandidate = (calcContainer: Group | null = null) => {
    if (!canv.canvasController.hashGrid) {
        // hashGrid должен быть всегда и сбда не должно попасть
        throw new Error("hashGrid is not defined");
    }

    const pt = canv.actionManager.getActionPoint();
    const itemsInCell = canv.canvasController.hashGrid.query(pt.x, pt.y);

    sortByIndexByParent(itemsInCell);

    let container = itemsInCell;

    if (!itemsInCell.length) {
        // кликнули в ячейку где есть группы
        container = canv.canvasController.hashGrid.queryByViewport();
    }

    if (calcContainer != null) {
        container = calcContainer.children as Group[];
    }

    let foundCandidate: Group | null = null;

    for (let j = container.length - 1; j >= 0; j--) {
        const candidate = container[j];

        if (candidate.isOverAnyInGroup(!!foundCandidate)) {
            if (!foundCandidate) {
                // Тут мы сперва берем родителя в том случае,
                // если у нас несколько элементов скреплено в группу,
                // если нет, то берем сам элемент
                foundCandidate = calcContainer !== null ? candidate : candidate.parent || candidate;
                continue;
            }

            if (candidate.children[0] instanceof frameWidget) {
                foundCandidate = candidate;
            }
        }
    }

    return foundCandidate;
};

export const createDuplicateGroup = (previousSelected: Group, clearText = true) => {
    const onlySelected = previousSelected;
    const states = [];

    onlySelected.collectEntitiesStates(states);

    const branchHead = canv.canvasController.createBranch(states)[0] as Group;

    branchHead.regenerateUIDS("");
    branchHead.fullUpdateDims();

    branchHead.arrows = [];

    if (clearText) branchHead.onlyChild()?.clearText();

    return branchHead;
};

export const getSelected = () => {
    if (canv.canvasController.selected.length === 0) {
        return null;
    }

    return canv.canvasController.selected[0].parent;
};

export const rotating = () => {
    const Cx = canv.canvasController.startX + canv.canvasController.width / 2;
    const Cy = canv.canvasController.startY + canv.canvasController.height / 2;
    const pt = canv.actionManager.getActionPoint();

    pt.x = pt.x * canv.m[0];
    pt.y = pt.y * canv.m[0];

    const prePt = canv.actionManager.getPreActionPoint();
    const preX = prePt.x * canv.m[0];
    const preY = prePt.y * canv.m[0];

    const startPointWidth = preX / canv.m[0] - Cx;
    const startPointHeight = Cy - preY / canv.m[0];
    const startTg = startPointHeight / startPointWidth;
    let startAngle = Math.atan(startTg) * (180 / Math.PI);

    if (startPointWidth < 0 && startPointHeight > 0) {
        startAngle = 180 + startAngle;
    } else if (startPointWidth < 0 && startPointHeight < 0) {
        startAngle = 180 + startAngle;
    } else if (startPointWidth > 0 && startPointHeight < 0) {
        startAngle = 360 + startAngle;
    }

    const endPointWidth = pt.x / canv.m[0] - Cx;
    const endPointHeight = Cy - pt.y / canv.m[0];
    const endTg = endPointHeight / endPointWidth;
    let endAngle = Math.atan(endTg) * (180 / Math.PI);

    if (endPointWidth < 0 && endPointHeight > 0) {
        endAngle = 180 + endAngle;
    } else if (endPointWidth < 0 && endPointHeight < 0) {
        endAngle = 180 + endAngle;
    } else if (endPointWidth > 0 && endPointHeight < 0) {
        endAngle = 360 + endAngle;
    }

    const deltaAngle = endAngle - startAngle;

    for (let i = 0; i < canv.canvasController.selected.length; i++) {
        canv.canvasController.selected[i].rotateGroup(deltaAngle, Cx, Cy);
    }

    canv.redraw();
};

export const startDragging = () => {
    // Очищаем менеджер overlapped групп перед началом нового перемещения
    frameOverlappedManager.clearOverlapped();

    for (let k = 0; k < canv.canvasController.selected.length; k++) {
        canv.canvasController.selected[k].groupTranslatingStart();
    }
};

export const startRotating = () => {
    for (let k = 0; k < canv.canvasController.selected.length; k++) {
        canv.canvasController.selected[k].groupRotatingStart();
    }
};

export const startResizing = () => {
    canv.canvasController.selected.forEach((item) => {
        item.groupResizingStart();
    });
};

export const drawRoundedRect = (
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    shouldFill: boolean,
) => {
    //canv.ctx.save();
    if (w < 2 * r) r = w / 2;
    if (h < 2 * r) r = h / 2;

    canv.ctx.beginPath();
    canv.ctx.moveTo(x + r, y);
    canv.ctx.arcTo(x + w, y, x + w, y + h, r);
    canv.ctx.arcTo(x + w, y + h, x, y + h, r);
    canv.ctx.arcTo(x, y + h, x, y, r);
    canv.ctx.arcTo(x, y, x + w, y, r);

    if (shouldFill) {
        canv.ctx.fill();
    } else {
        canv.ctx.stroke();
    }

    canv.ctx.closePath();
    //canv.ctx.restore();
};

export const isWidgetWithTextOrTextWidgetOnly = () => {
    if (!(canv.canvasController.selected.length === 1)) return false;

    const isItemWrapper = canv.canvasController.selected[0].isItemWrapper();
    if (!isItemWrapper || !canv.canvasController.selected[0].onlyChild()) {
        return false;
    }

    const constructorName = canv.canvasController.selected[0].onlyChild()?.constructorName;

    return (
        constructorName === "text" ||
        constructorName === "textV2" ||
        constructorName === "sticker" ||
        constructorName === "shape" ||
        constructorName === "mindMap" ||
        constructorName === "card"
    );
};
